import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';
import { rejectNotJoined } from '../utils';

const NOOP = () => {};

const selectMeeting = s => s.meeting;
const selectIsMeetingJoined = s => s.isMeetingJoined;
const selectRoomSwitchCount = s => s.roomSwitchCount;

// Append in place. Deliberately NOT `arr.push(...msgs)`: spread passes each
// message as a separate argument, so a batch larger than the engine's
// argument limit (~65k in V8) throws RangeError. The server currently caps a
// batch at 240 payloads, so the spread would be safe today — this loop means
// nobody has to re-check that assumption if the cap is ever raised.
function appendAll(arr, msgs) {
  for (let i = 0; i < msgs.length; i++) arr.push(msgs[i]);
}

function frontCap(arr, limit) {
  if (limit == null || !Number.isFinite(limit)) return;
  const max = Math.max(0, limit);
  const excess = arr.length - max;
  if (excess > 0) arr.splice(0, excess);
}

/**
 *
 * @param {string} topic
 * @param {{
 *  onMessageReceived?: Function,
 *  onBatchReceived?: Function,
 *  onOldMessagesReceived?: Function,
 *  onMessageDrop?: Function,
 * }} callbacks
 * @param {{
 *  oldMessageLimit?: number,
 *  realtimeOverflow?: 'queue' | 'drop',
 *  maxQueue?: number,
 *  newMessageLimit?: number,
 *  maxMessages?: number,
 *  bufferMessages?: boolean,
 * }} params
 * @returns {{
 *  publish: (message: string, options: {persist: boolean, sendOnly: Array<String>}, payload: object) => Promise<void>
 *  messages: Array<{
 *    id: string,
 *    message: string,
 *    senderId: string,
 *    senderName: string,
 *    timestamp: string,
 *    topic: string,
 *    payload: object,
 *  }>
 * }}
 */
const usePubSub = (
  topic,
  {
    onMessageReceived = NOOP,
    onBatchReceived = NOOP,
    onOldMessagesReceived = NOOP,
    onMessageDrop = NOOP
  } = {},
  {
    oldMessageLimit = null,
    realtimeOverflow = null,
    maxQueue = null,
    newMessageLimit = null,
    // Hard cap on the retained `messages` array. Prevents unbounded growth on
    // high-frequency topics by dropping the oldest messages once the cap is
    // reached. Pass `null` or `Infinity` to keep every message.
    maxMessages = null,
    // When false, incoming messages are delivered only via callbacks and the
    // `messages` array is never populated — for high-throughput topics (e.g.
    // thousands/sec) where retaining a re-rendering array is undesirable.
    bufferMessages = true
  } = {}
) => {
  const meeting = useMeetingSelector(selectMeeting);
  const isMeetingJoined = useMeetingSelector(selectIsMeetingJoined);
  const roomSwitchCount = useMeetingSelector(selectRoomSwitchCount);

  // Two independent FIFOs. Invariant when `maxMessages` is set:
  //   oldRef.length + realtimeRef.length <= maxMessages
  // Realtime is capped at max; old is capped at (max - realtime.length).
  const oldRef = useRef([]);
  const realtimeRef = useRef([]);
  // Bumped whenever either buffer mutates. `messages` is memoized on it so the
  // returned array keeps a stable identity between buffer changes — consumers
  // depend on that for `useEffect([messages])`, `useMemo`, and `React.memo`.
  const [version, forceUpdate] = useState(0);
  const isSubscribed = useRef(false);
  const listenerRef = useRef(null);

  const onMessageReceivedRef = useRef(onMessageReceived);
  const onBatchReceivedRef = useRef(onBatchReceived);
  const onOldMessagesReceivedRef = useRef(onOldMessagesReceived);
  const onMessageDropRef = useRef(onMessageDrop);
  const meetingRef = useRef(meeting);
  const maxMessagesRef = useRef(maxMessages);
  const bufferMessagesRef = useRef(bufferMessages);

  onMessageReceivedRef.current = onMessageReceived;
  onBatchReceivedRef.current = onBatchReceived;
  onOldMessagesReceivedRef.current = onOldMessagesReceived;
  onMessageDropRef.current = onMessageDrop;
  meetingRef.current = meeting;
  maxMessagesRef.current = maxMessages;
  bufferMessagesRef.current = bufferMessages;

  const reconcile = () => {
    const max = maxMessagesRef.current;
    if (max == null || !Number.isFinite(max)) return;
    frontCap(realtimeRef.current, max);
    frontCap(oldRef.current, max - realtimeRef.current.length);
  };

  const publish = useCallback(
    (message, options, payload) => {
      if (!meetingRef.current) return rejectNotJoined('publish');
      return meetingRef.current.pubSub.publish(
        topic,
        message,
        options,
        payload
      );
    },
    [topic]
  );

  // Per-message callback: fires user callback only. onBatchReceived owns the
  // realtime buffer to avoid double-counting (both carry the same messages).
  const _handlePubSub = useCallback(data => {
    onMessageReceivedRef.current(data);
  }, []);

  useEffect(() => {
    if (!meeting || isSubscribed.current || !isMeetingJoined) return;

    isSubscribed.current = true;

    // Set once the cleanup has run. `subscribe` is async, so an unmount (or a
    // `topic` change) can land while it is still in flight — React 18
    // StrictMode does exactly that on every mount. Without this, cleanup
    // unsubscribes first, the pending subscribe then resolves, and the SDK is
    // left holding a subscriber nothing will ever remove.
    let cancelled = false;

    const listener = {
      onMessageReceived: _handlePubSub,
      onBatchReceived: msgs => {
        onBatchReceivedRef.current(msgs);
        if (!bufferMessagesRef.current) return;
        appendAll(realtimeRef.current, msgs);
        reconcile();
        forceUpdate(v => v + 1);
      },
      onOldMessagesReceived: (msgs, meta) => {
        onOldMessagesReceivedRef.current(msgs, meta);
        if (!bufferMessagesRef.current) return;
        appendAll(oldRef.current, msgs);
        reconcile();
        forceUpdate(v => v + 1);
      },
      onMessageDrop: info => onMessageDropRef.current(info)
    };

    listenerRef.current = listener;

    const subscribeToPubsub = async () => {
      try {
        await meeting?.pubSub?.subscribe(topic, listener, {
          oldMessageLimit,
          realtimeOverflow,
          maxQueue,
          newMessageLimit
        });
        // Unsubscribed while this was in flight — undo it.
        if (cancelled) {
          await meetingRef.current?.pubSub?.unsubscribe(topic, listener);
        }
      } catch (error) {
        // Let a later effect run retry instead of leaving the hook wedged as
        // "subscribed" against a subscription the SDK never registered.
        if (!cancelled) isSubscribed.current = false;
      }
    };

    subscribeToPubsub();

    return () => {
      cancelled = true;
      isSubscribed.current = false;
      oldRef.current = [];
      realtimeRef.current = [];
      // Unawaited and rejectable (the SDK re-throws), so it needs its own
      // handler — an unhandled rejection here surfaces as a React error
      // overlay during teardown.
      Promise.resolve(
        meetingRef.current?.pubSub?.unsubscribe(topic, listenerRef.current)
      ).catch(error =>
        console.error(`usePubSub: unsubscribe from "${topic}" failed`, error)
      );
      listenerRef.current = null;
    };
  }, [meeting, isMeetingJoined, topic, _handlePubSub, roomSwitchCount]);

  const messages = useMemo(
    () => oldRef.current.concat(realtimeRef.current),
    // Both buffers are mutated in place, so `version` — not their contents — is
    // what marks them dirty. Every mutation site bumps it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [version]
  );

  return { publish, messages };
};

export default usePubSub;
